import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Upload,
  AppWindow,
  Users,
  Activity,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import AdminLiveStatus from "../components/AdminLiveStatus";
import AdminReports from "../components/AdminReports";

const emptyAppForm = { name: "", url: "", description: "", logoFile: null, logoPreview: "" };

function AppFormDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(emptyAppForm);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          name: editing.name || "",
          url: editing.url || "",
          description: editing.description || "",
          logoFile: null,
          logoPreview: editing.logo || "",
        });
      } else {
        setForm(emptyAppForm);
      }
    }
  }, [open, editing]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo file too large (max 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, logoFile: file, logoPreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.url.trim()) {
      toast.error("Name and URL are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("url", form.url.trim());
      fd.append("description", form.description.trim());
      if (form.logoFile) fd.append("logo", form.logoFile);

      if (editing) {
        await api.put(`/admin/apps/${editing.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("App updated");
      } else {
        await api.post("/admin/apps", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("App added");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Save failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="app-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "Edit app" : "Add a new app"}
          </DialogTitle>
          <DialogDescription>
            Add the name, link and a logo that your team will see.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="app-name">Name</Label>
            <Input
              id="app-name"
              data-testid="app-form-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Trello"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="app-url">URL</Label>
            <Input
              id="app-url"
              data-testid="app-form-url-input"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://trello.com"
              type="url"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="app-desc">Description (optional)</Label>
            <Textarea
              id="app-desc"
              data-testid="app-form-desc-input"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Project management"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center overflow-hidden ring-1 ring-black/5">
                {form.logoPreview ? (
                  <img
                    src={form.logoPreview}
                    alt="logo preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <AppWindow className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
                data-testid="app-form-logo-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                data-testid="app-form-upload-btn"
              >
                <Upload className="h-4 w-4 mr-1.5" />
                Upload image
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              PNG, JPG up to 2MB.
            </p>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="app-form-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="app-form-submit-btn"
              className="dp-gradient-bg text-white hover:opacity-90"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : editing ? (
                "Save changes"
              ) : (
                "Add app"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const emptyEmployeeForm = {
  name: "",
  email: "",
  password: "",
  is_manager: false,
  assigned_app_ids: [],
};

function EmployeeFormDialog({ open, onOpenChange, editing, apps, onSaved }) {
  const [form, setForm] = useState(emptyEmployeeForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          name: editing.name || "",
          email: editing.email || "",
          password: "",
          is_manager: !!editing.is_manager,
          assigned_app_ids: editing.assigned_app_ids || [],
        });
      } else {
        setForm(emptyEmployeeForm);
      }
    }
  }, [open, editing]);

  const toggleApp = (id) => {
    setForm((f) => {
      const has = f.assigned_app_ids.includes(id);
      return {
        ...f,
        assigned_app_ids: has
          ? f.assigned_app_ids.filter((x) => x !== id)
          : [...f.assigned_app_ids, id],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editing) {
        const body = {
          name: form.name.trim(),
          is_manager: form.is_manager,
          assigned_app_ids: form.assigned_app_ids,
        };
        if (form.password) body.password = form.password;
        await api.put(`/admin/employees/${editing.id}`, body);
        toast.success("Employee updated");
      } else {
        if (!form.email.trim() || !form.password) {
          toast.error("Email and password are required");
          setSubmitting(false);
          return;
        }
        await api.post("/admin/employees", {
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          is_manager: form.is_manager,
          assigned_app_ids: form.assigned_app_ids,
        });
        toast.success("Employee created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Save failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="employee-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "Edit employee" : "Add a new employee"}
          </DialogTitle>
          <DialogDescription>
            Create an account and choose which apps they can access.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emp-name">Name</Label>
              <Input
                id="emp-name"
                data-testid="employee-form-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp-email">Email</Label>
              <Input
                id="emp-email"
                data-testid="employee-form-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@dplus.ai"
                type="email"
                disabled={!!editing}
                required={!editing}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="emp-password">
              {editing ? "New password (leave blank to keep current)" : "Password"}
            </Label>
            <Input
              id="emp-password"
              data-testid="employee-form-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              type="password"
              required={!editing}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3 bg-muted/30">
            <div className="space-y-0.5">
              <Label htmlFor="emp-is-manager" className="font-medium">
                Manager access
              </Label>
              <p className="text-xs text-muted-foreground">
                Allow this employee to view the attendance dashboard and
                reports (read‑only).
              </p>
            </div>
            <Switch
              id="emp-is-manager"
              checked={form.is_manager}
              onCheckedChange={(v) => setForm({ ...form, is_manager: v })}
              data-testid="employee-form-manager-toggle"
            />
          </div>

          <div className="space-y-2">
            <Label>Assigned apps</Label>
            {apps.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No apps yet — add an app first to assign access.
              </p>
            ) : (
              <ScrollArea className="h-48 border border-border rounded-lg p-3">
                <div className="space-y-2">
                  {apps.map((app) => {
                    const checked = form.assigned_app_ids.includes(app.id);
                    return (
                      <label
                        key={app.id}
                        className="flex items-center gap-3 cursor-pointer hover:bg-muted/60 rounded-md p-2 transition-colors"
                        data-testid={`assign-app-${app.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleApp(app.id)}
                        />
                        <div className="h-7 w-7 rounded bg-muted flex items-center justify-center overflow-hidden">
                          {app.logo ? (
                            <img
                              src={app.logo}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <AppWindow className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{app.name}</span>
                      </label>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="employee-form-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="employee-form-submit-btn"
              className="dp-gradient-bg text-white hover:opacity-90"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : editing ? (
                "Save changes"
              ) : (
                "Create employee"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = !isAdmin && !!user?.is_manager;

  const [apps, setApps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [deleteAppId, setDeleteAppId] = useState(null);

  const [empDialogOpen, setEmpDialogOpen] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [deleteEmpId, setDeleteEmpId] = useState(null);

  const refresh = async () => {
    try {
      const calls = [api.get("/admin/employees")];
      if (isAdmin) calls.unshift(api.get("/admin/apps"));
      const results = await Promise.all(calls);
      if (isAdmin) {
        setApps(results[0].data);
        setEmployees(results[1].data);
      } else {
        setApps([]);
        setEmployees(results[0].data);
      }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const confirmDeleteApp = async () => {
    if (!deleteAppId) return;
    try {
      await api.delete(`/admin/apps/${deleteAppId}`);
      toast.success("App removed");
      setDeleteAppId(null);
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"
      );
    }
  };

  const confirmDeleteEmp = async () => {
    if (!deleteEmpId) return;
    try {
      await api.delete(`/admin/employees/${deleteEmpId}`);
      toast.success("Employee removed");
      setDeleteEmpId(null);
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"
      );
    }
  };

  return (
    <div className="min-h-screen dp-grain" data-testid="admin-dashboard">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 dp-fade-in">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
            {isManager ? "Manage" : "Admin"}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            {isManager ? "Attendance overview" : "Workspace settings"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isManager
              ? "View live status and reports for all employees."
              : "Manage apps and decide which employees can access them."}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="live" className="w-full">
            <TabsList data-testid="admin-tabs">
              <TabsTrigger value="live" data-testid="admin-live-tab">
                <Activity className="h-4 w-4 mr-1.5" /> Live
              </TabsTrigger>
              <TabsTrigger value="reports" data-testid="admin-reports-tab">
                <BarChart3 className="h-4 w-4 mr-1.5" /> Reports
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="apps" data-testid="admin-apps-tab">
                  <AppWindow className="h-4 w-4 mr-1.5" /> Apps ({apps.length})
                </TabsTrigger>
              )}
              <TabsTrigger value="employees" data-testid="admin-employees-tab">
                <Users className="h-4 w-4 mr-1.5" /> Employees (
                {employees.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="live" className="mt-6">
              <AdminLiveStatus />
            </TabsContent>

            <TabsContent value="reports" className="mt-6">
              <AdminReports />
            </TabsContent>

            {/* ===== APPS TAB (admin only) ===== */}
            {isAdmin && (
            <TabsContent value="apps" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-semibold">
                  Company apps
                </h2>
                <Button
                  onClick={() => {
                    setEditingApp(null);
                    setAppDialogOpen(true);
                  }}
                  data-testid="add-app-btn"
                  className="dp-gradient-bg text-white hover:opacity-90"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> Add app
                </Button>
              </div>

              <div className="border border-border rounded-xl bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>App</TableHead>
                      <TableHead className="hidden md:table-cell">URL</TableHead>
                      <TableHead className="hidden lg:table-cell">
                        Description
                      </TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apps.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-muted-foreground py-10"
                        >
                          No apps yet. Click "Add app" to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      apps.map((app) => (
                        <TableRow key={app.id} data-testid={`app-row-${app.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden ring-1 ring-black/5">
                                {app.logo ? (
                                  <img
                                    src={app.logo}
                                    alt={app.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <AppWindow className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                              <div className="font-medium">{app.name}</div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <a
                              href={app.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-[#2563EB] hover:underline truncate max-w-xs inline-block"
                            >
                              {app.url}
                            </a>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                            {app.description || "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingApp(app);
                                setAppDialogOpen(true);
                              }}
                              data-testid={`edit-app-btn-${app.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteAppId(app.id)}
                              data-testid={`delete-app-btn-${app.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
            )}

            {/* ===== EMPLOYEES TAB ===== */}
            <TabsContent value="employees" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-semibold">
                  Team members
                </h2>
                {isAdmin && (
                <Button
                  onClick={() => {
                    setEditingEmp(null);
                    setEmpDialogOpen(true);
                  }}
                  data-testid="add-employee-btn"
                  className="dp-gradient-bg text-white hover:opacity-90"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> Add employee
                </Button>
                )}
              </div>

              <div className="border border-border rounded-xl bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden sm:table-cell">
                        Email
                      </TableHead>
                      <TableHead>Apps</TableHead>
                      {isAdmin && (
                        <TableHead className="text-right">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={isAdmin ? 4 : 3}
                          className="text-center text-muted-foreground py-10"
                        >
                          No employees yet. Click "Add employee".
                        </TableCell>
                      </TableRow>
                    ) : (
                      employees.map((emp) => {
                        const assignedApps = apps.filter((a) =>
                          (emp.assigned_app_ids || []).includes(a.id)
                        );
                        return (
                          <TableRow
                            key={emp.id}
                            data-testid={`employee-row-${emp.id}`}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full dp-gradient-bg text-white flex items-center justify-center text-xs font-semibold">
                                  {(emp.name || emp.email)
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="font-medium truncate">
                                    {emp.name || "—"}
                                  </div>
                                  {emp.is_manager && (
                                    <Badge
                                      className="dp-gradient-bg text-white border-0 text-[10px]"
                                      data-testid={`manager-badge-${emp.id}`}
                                    >
                                      Manager
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                              {emp.email}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1.5">
                                {assignedApps.length === 0 ? (
                                  <span className="text-xs text-muted-foreground">
                                    No apps assigned
                                  </span>
                                ) : (
                                  assignedApps.slice(0, 4).map((a) => (
                                    <Badge
                                      key={a.id}
                                      variant="secondary"
                                      className="font-normal"
                                    >
                                      {a.name}
                                    </Badge>
                                  ))
                                )}
                                {assignedApps.length > 4 && (
                                  <Badge variant="outline">
                                    +{assignedApps.length - 4}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            {isAdmin && (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingEmp(emp);
                                  setEmpDialogOpen(true);
                                }}
                                data-testid={`edit-employee-btn-${emp.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteEmpId(emp.id)}
                                data-testid={`delete-employee-btn-${emp.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      <AppFormDialog
        open={appDialogOpen}
        onOpenChange={setAppDialogOpen}
        editing={editingApp}
        onSaved={refresh}
      />
      <EmployeeFormDialog
        open={empDialogOpen}
        onOpenChange={setEmpDialogOpen}
        editing={editingEmp}
        apps={apps}
        onSaved={refresh}
      />

      <AlertDialog
        open={!!deleteAppId}
        onOpenChange={(o) => !o && setDeleteAppId(null)}
      >
        <AlertDialogContent data-testid="delete-app-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this app?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the app for everyone. Employees will no longer see
              it on their dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-app-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteApp}
              data-testid="delete-app-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteEmpId}
        onOpenChange={(o) => !o && setDeleteEmpId(null)}
      >
        <AlertDialogContent data-testid="delete-employee-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this employee?</AlertDialogTitle>
            <AlertDialogDescription>
              They will lose access to the portal immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-employee-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEmp}
              data-testid="delete-employee-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
